import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Line from '../../Utilities/Line';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';
import { useContext, useMemo } from 'react';
import { SessionContext } from '../../Contexts/SessionContext';
import SurveyCountViewModel from '../ViewModels/SurveyCountsViewModel';
import useData from '../../Utilities/Hooks/UseData';
import formatDate from '../../Utilities/FormateDayjsToDateOnly';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import { FetchAction, useTripRateContext } from '../TripRateContext';
import DropDownViewModel from '../../Utilities/ViewModels/DropDownViewModel';
import { getFilteredSurveys } from '../../TripRateNew/TriprateFilters';
import Stack from '@mui/material/Stack';
import TableContainer from '@mui/material/TableContainer';
import CompactTable from '../../Utilities/CompactTable';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';
import WarningPopup from '../../Popups/WarningPopup';
import Paper from '@mui/material/Paper';

export class Icon {
    title: string;
    img: string;
    multi: boolean;
    atc: boolean;
    servicing: boolean;

    constructor(title: string, img: string, multi: boolean, atc: boolean, servicing: boolean) {
        this.title = title;
        this.img = img;
        this.multi = multi;
        this.atc = atc;
        this.servicing = servicing;
    }
}

const CountDisplay = ({ label, value, loading }) => (
    <Typography
        variant="body2"
        sx={{ display: "flex", alignItems: "center", minHeight: 24 }}
    >
        {label}:{" "}
        <Box flexGrow="1" />
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                ml: 0.5,
                minWidth: 30,
                height: 25,
            }}
        >
            {loading ? <Skeleton width={30} height={24} /> : value}
        </Box>
    </Typography>
);

export const icons: Icon[] = [
    new Icon('Total Vehicles', 'TotalVehicles.png', false, true, false),
    new Icon('Car', 'Car.png', false, false, false),
    new Icon('PSVs', 'PSV.png', false, false, false),
    new Icon('LGV', 'LGV.png', false, false, false),
    new Icon('OGVs', 'OGV.png', false, false, false),
    new Icon('Taxi', 'Taxi.png', false, false, false),
    new Icon('Cyclists', 'Cyclist.png', false, false, false),
    new Icon('Motorbike', 'Motorbike.png', false, false, false),
    new Icon('Scooters', 'Scooters.png', false, false, false),
    new Icon('Total People', 'TotalPeople.png', true, false, false),
    new Icon('Pedestrians', 'Pedestrians.png', true, false, false),
    new Icon('Bus & Tram Passengers', 'Bus&TramPassengers.png', true, false, false),
    new Icon('Occupants', 'Occupants.png', true, false, false),
    new Icon('Coach Passengers', 'CoachPassengers.png', true, false, false),
    new Icon('Rail Passengers', 'RailPassengers.png', true, false, false),
    new Icon('Bus Passengers', 'BusPassengers.png', true, false, false),
    new Icon('Public Transport Users', 'PTU.png', true, false, false),
    new Icon('Servicing', 'Servicing.png', true, false, true),
    new Icon('Tram', 'Tram.png', true, false, false),
    new Icon('Underground', 'Underground.png', true, false, false),
    new Icon('DLR', 'DLR.png', true, false, false),
    new Icon('Overground', 'Rail.png', true, false, false),
    new Icon('National Rail', 'NationalRail.png', true, false, false),
    new Icon('Water Service', 'WaterService.png', true, false, false),
];

export default function ModeTypeSelection() {
    const theme = useTheme();
    const { sessionState, setSessionField } = useContext(SessionContext);
    const { surveys } = useTripRateContext();
    const { data: surveyTypeOptions } = useData<DropDownViewModel[]>('TripRateFilterNew', 'GetSurveyTypesToIncludeOptions');
    const { data: baseSurveyTypes } = useData<DropDownViewModel[]>('TripRateFilterNew', 'GetServicingVehicleOptions');
    const selectedServiceVehicles = sessionState?.primaryFilters?.serviceVehicles ?? [];
    const selectedTypes = sessionState?.primaryFilters?.surveyTypes ?? [];

    const landuse = sessionState.tripRateMain;
    const subuse = sessionState.tripRateSub;
    const start = sessionState.landusePage.startDate;
    const end = sessionState.landusePage.endDate;

    const { data: surveyCount, loading } = useData<SurveyCountViewModel>(
        "TripRateFilterNew", landuse && subuse && start && end ? `GetSurveyCounts?landUse=${landuse}&subuse=${subuse}&start=${formatDate(start)}&end=${formatDate(end)}` : undefined
    );

    const handleModeChange = (event, checked) => {
        const isMultiModal = checked;
        setSessionField("landusePage.isMultiModal", isMultiModal);
    }

    const filteredSurveyTypes = useMemo(() => {
        const list = getFilteredSurveys(
            surveys,
            { fetchAction: 'SurveysToInclude' as FetchAction }
        );
        return list;
    }, [surveys, sessionState.primaryFilters, sessionState.secondaryFilters]);

    const surveyTypeCounts: DropDownViewModel[] = useMemo(() => {
        if (!Array.isArray(filteredSurveyTypes) || filteredSurveyTypes.length === 0 || !Array.isArray(surveyTypeOptions))
            return surveyTypeOptions ?? [];

        const countMap: Record<number, number> = {};
        for (const s of filteredSurveyTypes) {
            const t = (s as any).surveyType as number | undefined;
            if (t != null) countMap[t] = (countMap[t] ?? 0) + 1;
        }

        return surveyTypeOptions.map((opt) => {
            const count = countMap[opt.id] ?? 0;
            return { ...opt, label: `${opt.label.split('(')[0].trim()} (${count})` };
        });
    }, [filteredSurveyTypes, surveyTypeOptions]);

    const handleSurveyTypeChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const id = surveyTypeCounts[index].id;
        const updated = event.target.checked
            ? [...selectedTypes, id]
            : selectedTypes.filter((r: number) => r !== id);

        setSessionField('primaryFilters.surveyTypes', updated);
    };

    const filteredServiceSurveys = useMemo(() => getFilteredSurveys(
        surveys,
        { fetchAction: 'GetServiceVehicles' }
    ), [surveys, sessionState.primaryFilters]);

    const surveyTypes = useMemo(() => {
        if (!filteredServiceSurveys?.length || !baseSurveyTypes?.length) return baseSurveyTypes ?? [];
        return baseSurveyTypes.map(x => {
            const count = filteredServiceSurveys.filter(s => s.servicingVehicles === x.id).length;
            return { ...x, label: `${x.label.split('(')[0].trim()} (${count})` };
        });
    }, [filteredServiceSurveys, baseSurveyTypes]);


    const handleServicingChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const id = surveyTypes[index].id;

        const selectedValues = selectedServiceVehicles;

        const updated = e.target.checked
            ? [...selectedValues, id]
            : selectedValues.filter(x => x !== id);

        setSessionField('primaryFilters.serviceVehicles', updated);
    };

    const getCountFromLabel = (label: string) => {
        const match = label.match(/\((\d+)\)/);
        return match ? Number(match[1]) : 0;
    };

    const multiChecked = sessionState.landusePage.isMultiModal;
    const atcTest = sessionState.primaryFilters.surveyTypes.indexOf(2) >= 0 && (surveyTypeCounts.length > 0 ? getCountFromLabel(surveyTypeCounts[1].label) > 0 : false);
    const servicingTest = sessionState.primaryFilters.serviceVehicles.indexOf(null) == -1;
    const fail = sessionState.primaryFilters.surveyTypes.length == 0 || sessionState.primaryFilters.serviceVehicles.length == 0;

    const getStyle = (icon: Icon) => {
        const isActive =
            (multiChecked || !icon.multi) &&
            (!atcTest || icon.atc) &&
            (servicingTest || !icon.servicing)
            && !fail;

        const sx = {
            width: 35,
            height: 35,
            bgcolor: isActive ? theme.palette.primary.main : "gray",
        };

        return sx;
    };

    return (
        <Grid container>
            <Grid size={12} container alignItems="center" component={Paper}>
                <Grid size={12}>
                    <Typography variant="h6">Mode Types</Typography>
                </Grid>
                <Line />
                <Grid size={12} container columns={8}>
                    {icons.map((icon, iconIndex) =>
                        <Grid key={iconIndex} size={1} container justifyContent="center">
                            <Grid>
                                <Tooltip title={icon.title}>
                                    <Avatar
                                        variant="rounded"
                                        sx={getStyle(icon)}
                                        src={`/Icons/${icon.img}`} />
                                </Tooltip>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
                <Grid size={5}>
                    <CountDisplay label="Manual Surveys" value={surveyCount?.vehicleManualCount} loading={loading} />
                    <CountDisplay label="ATC Surveys" value={surveyCount?.vehicleATCCount} loading={loading} />
                </Grid>
                <Grid size={2}>
                    <Switch checked={sessionState.landusePage?.isMultiModal ?? undefined} onChange={handleModeChange} />
                </Grid>
                <Grid size={5}>
                    <CountDisplay label="Multi-Modal Surveys" value={surveyCount?.vehicleMultiCount} loading={loading} />
                </Grid>
            </Grid>
            <Grid size={12} container alignItems="center" component={Paper}>
                <Grid size={12}>
                    <Typography variant="h6">Mode Filters</Typography>
                </Grid>
                <Line />
                <Grid size={12}>
                    <Stack spacing={1} direction="row" alignItems="center">
                        <TableContainer>
                            <CompactTable>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Count</TableCell>
                                        <TableCell>Include</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {surveyTypeCounts.map((row, index) => {
                                        const count = getCountFromLabel(row.label);
                                        const isChecked = selectedTypes.includes(row.id);

                                        return (
                                            <TableRow key={row.id}>
                                                <TableCell>{row.label.split('(')[0].trim()}
                                                    {row.label.indexOf("ATC") >= 0 &&
                                                        <WarningPopup
                                                            title="Note on ATC Surveys"
                                                            content="Note that the inclusion of ATC surveys in a selected set of surveys would mean that only the Total Vehicles trip rate calculation could be undertaken, as ATC surveys are not split into the standard TRICS vehicle sub-categories. If trip rates are required for the different vehicle types, then ATC surveys will need to be deselected."
                                                            type="warning"
                                                        />}
                                                </TableCell>
                                                <TableCell>{count}</TableCell>
                                                <TableCell>
                                                    <Checkbox checked={isChecked} onChange={handleSurveyTypeChange(index)} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {surveyTypes.map((row, index) => {
                                        const count = getCountFromLabel(row.label);
                                        const isChecked = selectedServiceVehicles.includes(row.id);
                                        return (
                                            <TableRow key={row.id}>
                                                <TableCell>{row.label.split('(')[0].trim()}
                                                    {row.label.indexOf("Included") >= 0 &&
                                                        <WarningPopup
                                                            title="Warning on Servicing Vehicles"
                                                            content='Users should note that any surveys that had a Servicing Vehicles count included, even if no servicing vehicle activity took place during the counts, will be included within the "included" figure shown, with surveys that did not have a Servicing Vehicles count observed shown in the "excluded" figure shown. This does not mean that any servicing did not take place at any of the "excluded" surveys, it just means that a record of servicing activity at those sites could not be obtained. For all TRICS surveys, all vehicles arriving and departing from sites are always included, with any Servicing Vehicles counts available being an extract of this total count.'
                                                            type="warning"
                                                        />}
                                                </TableCell>
                                                <TableCell>{count}</TableCell>
                                                <TableCell>
                                                    <Checkbox checked={isChecked} onChange={handleServicingChange(index)} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </CompactTable>
                        </TableContainer>


                    </Stack>
                </Grid>
            </Grid>
        </Grid>
    );
}